import { Injectable, Injector } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ISite } from 'src/app/Store/sites.state/sites.model';
import { WebshareApiSvc } from './webshare.api.svc';
import { IScanProjectProperties } from 'src/app/Store/layers.state/layers.model';
import { ButtonInfo } from 'src/app/common/UI-Components/helperClasses/value-and-display.class';
import { IProject } from 'src/app/common/Forms/Dialogs/import-scans-dialog/import-scans-util';
import { IPointCloudSession, IWebShareConf, PointCloudProvider } from 'src/app/common/Models/UI/point-cloud.interface';
import { ISseMsg, ModificationType } from './SSE/sseHandler.interface';
import { SphereXGApiService } from './sphereXG.api.service';
import { SCENE_MODE } from 'src/app/Store/app.state/app.model';
import { environment } from 'src/environments/environment';
import { ICompanyInfo } from 'src/app/common/Models/UI/company-info.interface';
import { Store } from '@ngxs/store';
import { take } from 'rxjs/operators';
import { CMD_ACTIONS, CMD_TARGETS, CmdRouterService } from '../cmd-router.service';

@Injectable({
  providedIn: 'root'
})
export class PointCloudSvc {
  private service: WebshareApiSvc | SphereXGApiService;
  public pointCloudProvider: string;
  private pointCloudAuthPending: boolean = false;
  public isPointCloudViewerDisplayed$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly injector: Injector,
    private readonly store: Store,
    private readonly cmdRouterSvc: CmdRouterService,
  ) {}

  public setPointCloudProvider(): void {
    const companyInfo: ICompanyInfo = this.store.selectSnapshot<ICompanyInfo>((state: any) => state.StateApp.companyInfo);
    const webshareCompanies: string[] = environment.webshareCompanies;
    if (webshareCompanies.includes(companyInfo.name.toLowerCase())) {
      this.pointCloudProvider = PointCloudProvider.Webshare;
      this.service = this.injector.get(WebshareApiSvc);
    } else {
      this.pointCloudProvider = PointCloudProvider.SphereXG;
      this.service = this.injector.get(SphereXGApiService);
    }
  }

  handleIncomingMsg(msg: ISseMsg): void {
    console.log('SSE GOT [PointCloudSvc]', msg);
    switch (msg.modificationType) {
      case ModificationType.CREATE: {
        this.service.pointCloudConfsCreated(msg.object, true);
        break;
      }
      case ModificationType.UPDATE: {
        this.service.pointCloudConfsUpdated(msg.object, true);
        break;
      }
      case ModificationType.DELETE: {
        this.service.pointCloudConfsDeleted(msg.object, true);
        break;
      }
    }
  }

  public getProjects(): Observable<any> {
    return this.service.getProjects();
  }

  public getScans(project: IProject): Observable<any> {
    return this.service.getScans(project);
  }

  public async getPointCloudId(selectedProject: any): Promise<any> {
    return this.service.getPointCloudId(selectedProject);
  }

  public async loginToPointCloud(testWebshareConf?: IWebShareConf, tourSite?: ISite): Promise<any> {
    return new Promise((resolve, reject) => {
      this.service.pointCloudLoginInfoInternal().pipe(take(1)).subscribe((success: boolean) => {
        if (success) {
          resolve(success);
        } else {
          reject(new Error(`PointCloud login failed with Error: ${success}`));
        }
      });
      this.service.loginToPointCloud(testWebshareConf, tourSite);
    });
  }

  public cancelCheckConnection(): void {
    this.service.cancelCheckConnection();
  }

  public async showPointCloud(sceneMode: SCENE_MODE, scanProjectProperties: IScanProjectProperties, scanId: string, phi: number, theta: number, fov: number): Promise<void> {
    if (this.pointCloudAuthPending) {
      return null;
    }
    this.pointCloudAuthPending = false;
    try {
      await this.loginToPointCloud();
      this.service.showPointCloud(sceneMode, scanProjectProperties, scanId, phi, theta, fov);
    } catch (error) {
      console.error('Error during point cloud login:', error);
    } finally {
      this.pointCloudAuthPending = false;
    }
  }

  public async openPlacemark(placemarkId: any, placemarkType: any, layerId: any) {
    if (this.pointCloudAuthPending) {
      return;
    }
    this.pointCloudAuthPending = true;
    try {
      await this.loginToPointCloud();
      await this.checkUserAccessToProject(layerId);
      this.cmdRouterSvc.sendActionCmd(CMD_TARGETS.PANORAMIC_MANAGER, CMD_ACTIONS.GO_TO_SCAN_PM, {pmId: placemarkId, pmType: placemarkType});
    } catch (error) {
      console.error('Error during point cloud login:', error);
    } finally {
      this.pointCloudAuthPending = false;
    }
  }

  public async checkUserAccessToProject(layerId: string, scanProjectProperties?: IScanProjectProperties): Promise<any> {
    return this.service.checkUserAccessToProject(layerId, scanProjectProperties);
  }

  public getScanPlacemarkUrl(scanProjectProperties: IScanProjectProperties, scanId: string): string {
    return this.service.getScanPlacemarkUrl(scanProjectProperties, scanId);
  }

  public getResoultionOptions(): ButtonInfo[] {
    return this.service.getResoultionOptions();
  }

  public getDefaultResolution(): string {
    return this.service.getDefaultResolution();
  }

  public openWebPointCloudConfDialog(): void {
    this.service.openWebPointCloudConfDialog();
  }

  public setPointCloudSession(pointCloudSession: IPointCloudSession): void {
    this.service.setPointCloudSession(pointCloudSession);
  }

  public clearPointCloudSession(): void {
    this.service.clearPointCloudSession();
  }

  public getAllPointCloudConfs(): any[] {
    return this.service.getAllPointCloudConfs();
  }

  public getPointCloudConfsSelectionOpts(): ButtonInfo[] {
    return this.service.getPointCloudConfsSelectionOpts();
  }

  public getPointCloudConfById(pointCloudConfId: any): any {
    return this.service.getPointCloudConfById(pointCloudConfId);
  }

  public showTestPointCloudConnection(pointCloudConf: any): boolean {
    return this.service.showTestPointCloudConnection(pointCloudConf);
  }

  public async testPointCloudConnection(pointCloudConf: any): Promise<any> {
    return await this.service.testPointCloudConnection(pointCloudConf);
  }

  public getPointCloudConfIdFromSite(site: any): number {
    return this.service.getPointCloudConfIdFromSite(site);
  }

  public setPointCloudConfIdInSite(site: any): void {
    this.service.setPointCloudConfIdInSite(site);
  }

}
